import moment from "moment";
import { clone } from "../../Shared/utils/clone";
import { SelectionMode, } from "../components/modules/MealBundleChooser/MealBundleChooser-Types";
import { createTemplateFromBundle, processBundles, } from "../components/modules/MealBundleChooser/mealBundleChooserUtils";
import { createAllMealSizeOption } from "../components/modules/MealSize/mealSizeUtils";
import { maybeAddUpchargesToAnswers } from "../components/modules/Questions/useQuestionsModule";
import { DEFAULT_SUBSCRIPTION_PERIOD, MAX_DAY_COUNT, ModuleTypes, OrderFlowText, RecipeSelectionType, SubscriptionPeriodSetting, } from "../OrderFlowProvider/OrderFlowTypes";
import { calculateSubscriptionPeriod, mergeNewAndExistingMeals } from "../OrderFlowProvider/reducerUtils";
import { getTemplateDays } from "../utils/orderFlowUtils";
export var DefaultSelection;
(function (DefaultSelection) {
    DefaultSelection[DefaultSelection["None"] = 0] = "None";
})(DefaultSelection || (DefaultSelection = {}));
/**
 * Defines various helper functions used throughout Order Flow
 */
export const useHelpers = () => {
    const synchronizeActiveCartProducts = (cartProducts, modelProducts) => {
        if (!cartProducts || !modelProducts) {
            return;
        }
        /* Remove cart products that do not map to a model product */
        cartProducts.forEach((cartProduct, index, object) => {
            let modelProduct = modelProducts.find((modelProduct) => cartProduct.productId == modelProduct.productId && cartProduct.DayOfWeek == modelProduct.DayOfWeek);
            if (!modelProduct) {
                object.splice(index, 1);
            }
            else {
                modelProduct.isRenewing = cartProduct.isRenewing;
                modelProduct.quantity = cartProduct.quantity;
                cartProducts[index] = modelProduct;
            }
        });
        /* Add model products that are not included in the cart */
        modelProducts.forEach((modelProduct) => {
            let cartProduct = cartProducts.find((cartProduct) => cartProduct.productId == modelProduct.productId && cartProduct.DayOfWeek == modelProduct.DayOfWeek);
            if (!cartProduct) {
                cartProducts.push(modelProduct);
            }
        });
    };
    const synchronizeCartQuestionAnswers = (cartQuestionAnswers, questionModules) => {
        if (!cartQuestionAnswers) {
            return;
        }
        const questions = questionModules
            .map((mod) => mod.ModuleConfig)
            .map((config) => config.QuestionBuilder)
            .flat();
        /* Remove questions that do not map to a flow question */
        cartQuestionAnswers.forEach((cartAnswer, index, object) => {
            let flowQuestion = questions.find((question) => question.QuestionSKU === cartAnswer.QuestionSKU);
            if (!flowQuestion) {
                object.splice(index, 1);
            }
        });
        /* Add questions that are not included in the cart */
        questions.forEach((question) => {
            const cartAnswer = cartQuestionAnswers.find((answer) => answer.QuestionSKU === question.QuestionSKU);
            const newAnswerObject = maybeAddUpchargesToAnswers(question, cartAnswer === null || cartAnswer === void 0 ? void 0 : cartAnswer.Answer);
            if (cartAnswer) {
                // If cartAnswer exists, replace it with object containing upcharge
                const index = cartQuestionAnswers.indexOf(cartAnswer);
                if (index !== -1) {
                    cartQuestionAnswers[index] = newAnswerObject;
                }
            }
            else {
                // If cartAnswer doesn't exist, add a new object with the default value
                cartQuestionAnswers.push(newAnswerObject);
            }
        });
    };
    const getDefaultCart = (config, pageToStart, subscriptionPeriod) => {
        let { DeliveryDays: deliveryDays, Products: products } = config;
        const { Flow: flow, OmissionConfig: omissionConfig, RecipeTypes: recipeTypes, OrderFlowDefaults: orderFlowDefaults, StartSunday: startSunday, Program: program, MealSizes: mealSizes, SubscriptionMeals: SubscriptionMeals, SubscriptionRecipeType: SubscriptionRecipeType, IsEditFlow: IsEditFlow, GeneralSettings: GeneralSettings, } = config;
        const { OmissionsToDisplay: omissionsToDisplay } = omissionConfig;
        const cartProducts = clone(products);
        const defaultStartDate = deliveryDays && deliveryDays.length ? moment(deliveryDays[0].MealDate) : undefined;
        const defaultDaysOfWeek = config.GeneralSettings.OnlyShowDeliveryDays
            ? config.DeliverySchedule.map((ds) => ds.shipDayOfWeek)
            : config.DeliveryScheduleMealDays;
        const defaultCutoffDay = getCutoffDateForMealDate(moment(defaultStartDate), deliveryDays);
        let defaultCart = {
            startDate: defaultStartDate,
            daysOfWeek: defaultDaysOfWeek,
            template: [],
            omissions: [],
            selectedDay: defaultStartDate,
            cutoffDay: defaultCutoffDay,
            isSubscription: false,
            products: cartProducts !== null && cartProducts !== void 0 ? cartProducts : [],
            currentPage: pageToStart,
            sku: config.Sku,
            totalPages: 0,
            subscriptionPeriod: subscriptionPeriod !== null && subscriptionPeriod !== void 0 ? subscriptionPeriod : 1,
            subscriptionPeriodSetting: SubscriptionPeriodSetting.MATCH_SUBSCRIPTION_PERIOD,
            oneTimeOrderPeriod: undefined,
            meals: [],
            bundles: [],
            questionAnswers: [],
            additionalItems: [],
        };
        if (orderFlowDefaults.StartDate) {
            defaultCart.startDate = moment(orderFlowDefaults.StartDate);
        }
        if (IsEditFlow) {
            defaultCart.isSubscription = true;
        }
        else if (orderFlowDefaults.IsSubscription) {
            defaultCart.isSubscription = orderFlowDefaults.IsSubscription;
        }
        if (orderFlowDefaults.RecipeType) {
            defaultCart.recipeType =
                recipeTypes && recipeTypes.find((rt) => rt.recipeTypeName === orderFlowDefaults.RecipeType);
        }
        if (orderFlowDefaults.MealSize) {
            defaultCart.mealSize = mealSizes.find((ms) => { var _a; return ms.RecipeSizeId === ((_a = orderFlowDefaults === null || orderFlowDefaults === void 0 ? void 0 : orderFlowDefaults.MealSize) === null || _a === void 0 ? void 0 : _a.RecipeSizeId); });
        }
        defaultCart.totalPages = flow.FlowModules.map((flowModule) => flowModule.Page).reduce((a, b) => Math.max(a, b));
        //Ensure we set the Subscription module before the Day Quantity Module
        const desiredOrderOfModules = [
            ModuleTypes.SUBSCRIPTION,
            ModuleTypes.DAYS_OF_WEEK,
            ModuleTypes.DAY_QUANTITY,
            ModuleTypes.WEEK_QUANTITY,
            ModuleTypes.DAY_FILTER,
            ModuleTypes.MEAL_SIZE,
            ModuleTypes.MEAL_BUNDLE_CHOOSER,
        ];
        const orderedFlowModules = flow.FlowModules.sort((a, b) => {
            const indexA = desiredOrderOfModules.indexOf(a.Module.ModuleType);
            const indexB = desiredOrderOfModules.indexOf(b.Module.ModuleType);
            // If either is not found, push them to the end of the array
            if (indexA === -1)
                return 1;
            if (indexB === -1)
                return -1;
            return indexA - indexB;
        });
        //Set default values for the flow modules
        orderedFlowModules.forEach((flowModule) => {
            const moduleType = flowModule.Module.ModuleType;
            let mealSize;
            const mealQuantityModuleConfig = flowModule.ModuleConfig;
            const omissionModuleConfig = flowModule.ModuleConfig;
            let defaultOmissions = [];
            switch (moduleType) {
                case ModuleTypes.DAYS_OF_WEEK: {
                    const daysOfWeekModuleConfig = flowModule.ModuleConfig;
                    // Sort the delivery days based on the configuration. If StartSunday is false,
                    // push Sunday (represented by 0) to the end of the list.
                    const sortedDeliveryDays = config.DeliveryScheduleMealDays.sort(function (a, b) {
                        if (config.StartSunday === false) {
                            // If 'a' is Sunday (0), place it after 'b'.
                            if (a == 0)
                                return 1;
                            // If 'b' is Sunday (0), place it before 'a'.
                            if (b == 0)
                                return -1;
                        }
                        // Default sorting is numeric (ascending).
                        return a - b;
                    });
                    // Filter the default day selections, keeping only the days
                    // that are part of the delivery schedule.
                    const daysInDeliverySchedule = daysOfWeekModuleConfig.DefaultDaySelections.filter((dayOfWeek) => sortedDeliveryDays.includes(dayOfWeek));
                    defaultCart.daysOfWeek = daysInDeliverySchedule;
                    if (defaultCart.subscriptionPeriodSetting === SubscriptionPeriodSetting.MATCH_ORDER_PERIOD) {
                        defaultCart.subscriptionPeriod = calculateSubscriptionPeriod(defaultCart.daysOfWeek.length, defaultCart.daysOfWeek);
                    }
                    break;
                }
                case ModuleTypes.MEAL_QUANTITY: {
                    if (IsEditFlow) {
                        defaultCart.template = SubscriptionMeals;
                    }
                    else {
                        const defaultMealDayQuantities = mealQuantityModuleConfig.DefaultMealDayQuantities;
                        const template = JSON.parse(defaultMealDayQuantities);
                        template.forEach((templateMeal) => {
                            const matchingProgramMeal = program.ProgramMeals.find((pm) => {
                                return pm.ProgramMealId == templateMeal.ProgramMealId;
                            });
                            if (matchingProgramMeal) {
                                templateMeal.Price = matchingProgramMeal.Price;
                            }
                        });
                        if (mealQuantityModuleConfig.SameMealQuantitiesEachMealDay && template.length) {
                            // Get the DayOfWeek value from the first meal in the template to determine
                            // which day's meals to copy.
                            const dayToCopy = template[0].DayOfWeek;
                            // Filter the template to get all the meals that are assigned to the same
                            // day as 'dayToCopy' (i.e., the meals to copy across all days).
                            const mealsToCopy = template.filter((m) => m.DayOfWeek === dayToCopy);
                            const newTemplate = [];
                            // This loop will create a copy of 'templateMeal' for each day of the week (0 to 6)
                            mealsToCopy.forEach((templateMeal) => {
                                // Loop through numbers 0 to 6 (representing each day of the week, from Sunday (0) to Saturday (6)).
                                for (let i = 0; i <= 6; i++) {
                                    newTemplate.push({
                                        ProgramMealId: templateMeal.ProgramMealId,
                                        ProgramMealName: templateMeal.ProgramMealName,
                                        Price: templateMeal.Price,
                                        DayOfWeek: i,
                                        Quantity: templateMeal.Quantity,
                                        MealSize: templateMeal.MealSize,
                                        MealTypeId: templateMeal.MealTypeId
                                    });
                                }
                            });
                            defaultCart.template = newTemplate;
                        }
                        else {
                            defaultCart.template = template;
                        }
                        if (mealQuantityModuleConfig.ToggleMode) {
                            defaultCart.toggleTemplate = [...defaultCart.template];
                            defaultCart.template = [];
                        }
                    }
                    break;
                }
                case ModuleTypes.OMISSION: {
                    if (omissionsToDisplay) {
                        omissionsToDisplay.forEach((category) => {
                            if (category.dislikes) {
                                category.dislikes.forEach((dislike) => {
                                    if ((IsEditFlow && dislike.selected === true) || (!IsEditFlow && dislike.required === true)) {
                                        defaultOmissions.push({
                                            categoryId: category.categoryId,
                                            categoryName: category.categoryId == 0 ? omissionModuleConfig.NoCategoryText : category.categoryName,
                                            dislikeId: dislike.dislikeId,
                                            dislikeName: dislike.dislikeName,
                                            AllowedRecipeTypes: dislike.AllowedRecipeTypes,
                                        });
                                    }
                                });
                            }
                        });
                    }
                    defaultCart.omissions = defaultOmissions;
                    break;
                }
                case ModuleTypes.RECIPE_TYPE: {
                    const recipeTypeModuleConfig = flowModule.ModuleConfig;
                    const allRecipeOption = recipeTypes.find((rt) => rt.isAllRecipeType);
                    let recipeType;
                    if (IsEditFlow) {
                        recipeType = SubscriptionRecipeType;
                    }
                    else if (recipeTypeModuleConfig.DefaultSelected === DefaultSelection.None &&
                        allRecipeOption === undefined) {
                        recipeType = DefaultSelection.None;
                    }
                    else if (recipeTypeModuleConfig.DefaultSelected === DefaultSelection.None &&
                        allRecipeOption !== undefined) {
                        recipeType = allRecipeOption;
                    }
                    else {
                        recipeType =
                            recipeTypes && recipeTypes.find((rt) => rt.recipeTypeId === recipeTypeModuleConfig.DefaultSelected);
                    }
                    if (recipeType) {
                        defaultCart.recipeType = recipeType;
                    }
                    break;
                }
                case ModuleTypes.MEAL_SIZE: {
                    // These options are not editable/in the edit flow.
                    const { AllMealSizeOption, DefaultSelected } = flowModule.ModuleConfig;
                    // If a default meal size is selected: find and assign the default value.
                    if (DefaultSelected !== DefaultSelection.None) {
                        mealSize = mealSizes && mealSizes.find((ms) => ms.RecipeSizeId === DefaultSelected);
                    }
                    // If there is no default selection, but the all meal size options is selected,
                    // return the all meal size option as the initial value.
                    if (DefaultSelected === DefaultSelection.None && !!AllMealSizeOption) {
                        mealSize = createAllMealSizeOption(AllMealSizeOption.TextValue);
                    }
                    // If either of the above cases is true, we return that value.
                    if (mealSize) {
                        defaultCart.mealSize = mealSize;
                    }
                    // Add the all meal size option to the array of meal sizes.
                    if (AllMealSizeOption)
                        mealSizes.unshift(createAllMealSizeOption(AllMealSizeOption.TextValue));
                    break;
                }
                case ModuleTypes.HTML: {
                    break;
                }
                case ModuleTypes.DAY_QUANTITY: {
                    const dayQuantityModuleConfig = flowModule.ModuleConfig;
                    if (dayQuantityModuleConfig.DayQuantityOptions && dayQuantityModuleConfig.DayQuantityOptions.length) {
                        defaultCart.dayQuantity = dayQuantityModuleConfig.DayQuantityOptions[0].value;
                    }
                    //Day quantity module sets the subscription period
                    if (defaultCart.subscriptionPeriodSetting === SubscriptionPeriodSetting.MATCH_ORDER_PERIOD) {
                        defaultCart.subscriptionPeriod = calculateSubscriptionPeriod(dayQuantityModuleConfig.DayQuantityOptions[0].value, defaultCart.daysOfWeek);
                    }
                    break;
                }
                case ModuleTypes.PRODUCT: {
                    const productModuleConfig = flowModule.ModuleConfig;
                    if (productModuleConfig.RenewByDefault) {
                        defaultCart.products = defaultCart.products.map((p) => {
                            p.isRenewing = true;
                            return p;
                        });
                    }
                    break;
                }
                case ModuleTypes.SUBSCRIPTION: {
                    const subscriptionModuleConfig = flowModule.ModuleConfig;
                    defaultCart.isSubscription = subscriptionModuleConfig.IsSubscription;
                    defaultCart.subscriptionPeriodSetting = subscriptionModuleConfig.SubscriptionPeriodSetting;
                    //Set to configured subscription period
                    if (subscriptionModuleConfig.SubscriptionPeriodSetting === SubscriptionPeriodSetting.MATCH_SUBSCRIPTION_PERIOD) {
                        defaultCart.subscriptionPeriod = subscriptionModuleConfig.SubscriptionPeriod;
                    }
                    break;
                }
                case ModuleTypes.WEEK_QUANTITY: {
                    const weekQuantityModuleConfig = flowModule.ModuleConfig;
                    if (weekQuantityModuleConfig.WeekQuantityOptions && weekQuantityModuleConfig.WeekQuantityOptions.length) {
                        defaultCart.oneTimeOrderPeriod = weekQuantityModuleConfig.WeekQuantityOptions[0].value;
                    }
                    break;
                }
                case ModuleTypes.MEAL_BUNDLE_CHOOSER: {
                    const mealBundleModuleConfig = flowModule.ModuleConfig;
                    const isOrderFlowAndSingleSelect = !IsEditFlow && mealBundleModuleConfig.MealBundleSelectionMode === SelectionMode.Single;
                    // Set price and meal size on bundle meals with matching programMeal
                    mealBundleModuleConfig.BundleBuilder = mealBundleModuleConfig.BundleBuilder.map((bundle) => {
                        return Object.assign(Object.assign({}, bundle), { BundleMeals: bundle.BundleMeals.map((bm) => {
                                var _a;
                                const programMeal = config.Program.ProgramMeals.find((pm) => pm.ProgramMealId === bm.ProgramMealId);
                                return Object.assign(Object.assign({}, bm), { Price: (_a = programMeal === null || programMeal === void 0 ? void 0 : programMeal.Price) !== null && _a !== void 0 ? _a : 0, MealSize: programMeal === null || programMeal === void 0 ? void 0 : programMeal.MealSize });
                            }) });
                    });
                    const defaultSelectedBundle = mealBundleModuleConfig.BundleBuilder.find((bundle) => bundle.BundleName == mealBundleModuleConfig.DefaultMealBundleSelection);
                    //Flag a bundle to be selected by default if one is configured || OrderFlow Only || Single Selection Mode Only
                    if (isOrderFlowAndSingleSelect && defaultSelectedBundle) {
                        const selectedBundleWithFilteredMeals = Object.assign(Object.assign({}, defaultSelectedBundle), { BundleMeals: defaultSelectedBundle.BundleMeals.filter((bm) => { var _a; return !((_a = defaultCart.mealSize) === null || _a === void 0 ? void 0 : _a.Size) || bm.MealSize === defaultCart.mealSize.Size; }) });
                        selectedBundleWithFilteredMeals.Quantity = 1;
                        selectedBundleWithFilteredMeals.BundleTemplate = createTemplateFromBundle([
                            selectedBundleWithFilteredMeals,
                        ]);
                        defaultCart.bundles = [selectedBundleWithFilteredMeals];
                        const shouldSetSubPeriod = selectedBundleWithFilteredMeals.EnableDayQuantity &&
                            defaultCart.subscriptionPeriodSetting === SubscriptionPeriodSetting.MATCH_ORDER_PERIOD;
                        // The subscriptionPeriod and dayQuantity are set from the bundle only in single-select mode,
                        // and only if the bundle has a defined Day Quantity.
                        if (shouldSetSubPeriod && selectedBundleWithFilteredMeals.BundleDayQuantity) {
                            defaultCart.subscriptionPeriod = calculateSubscriptionPeriod(selectedBundleWithFilteredMeals.BundleDayQuantity, defaultCart.daysOfWeek);
                            defaultCart.dayQuantity = selectedBundleWithFilteredMeals.BundleDayQuantity;
                        }
                        const { template, meals } = processBundles(defaultCart.dayQuantity, config.DeliveryDays, config.StartSunday, defaultCart.subscriptionPeriod, defaultCart.oneTimeOrderPeriod, config.GeneralSettings.OnlyShowDeliveryDays, defaultCart.meals, selectedBundleWithFilteredMeals.BundleMeals, [selectedBundleWithFilteredMeals], getMealsFromTemplate, defaultCart.startDate, defaultCart.isSubscription, defaultCart.daysOfWeek);
                        defaultCart.template = template;
                        defaultCart.meals = meals;
                    }
                    //We don't know which bundle the user selected on Order Flow
                    //so we have to compare the Bundles to the Subscription meals to figure it out
                    if (IsEditFlow) {
                        //Filter out bundles whose meals to not match subscription meals
                        const filteredBundles = mealBundleModuleConfig.BundleBuilder.filter((bundle) => bundle.BundleMeals.find((bm) => SubscriptionMeals.find((sm) => sm.ProgramMealId === bm.ProgramMealId))).map((bundle) => {
                            const dayQuantity = Number(bundle.BundleDayQuantity) || defaultCart.dayQuantity;
                            // Filter the meals in the bundle to include only those that match the subscription meals
                            let filteredMeals = bundle.BundleMeals.filter((bm) => SubscriptionMeals.find((sm) => sm.ProgramMealId === bm.ProgramMealId && bm.DayOfWeek === sm.DayOfWeek));
                            if (dayQuantity === 1) {
                                filteredMeals = filteredMeals.filter((m) => m.DayOfWeek === moment(defaultCart.startDate).day());
                            }
                            return Object.assign(Object.assign({}, bundle), { BundleMeals: filteredMeals, Quantity: 1 });
                        });
                        // Create a lookup for the required meals.
                        // Keys are program meal id and values are the meal quantity.
                        const requiredMeals = {};
                        SubscriptionMeals.forEach(({ ProgramMealId, Quantity }) => {
                            requiredMeals[ProgramMealId] = Quantity;
                        });
                        //Recursively check bundle permutations using backtracking
                        const bundlesFromBacktracking = findBundles(filteredBundles, requiredMeals);
                        //Could not find a matching bundle so set template with subscription meals
                        if (!bundlesFromBacktracking.length) {
                            defaultCart.template = SubscriptionMeals;
                        }
                        else {
                            //Add the bundles to the cart and set template
                            defaultCart.bundles = bundlesFromBacktracking;
                            defaultCart.template = createTemplateFromBundle(bundlesFromBacktracking);
                        }
                    }
                    break;
                }
                case ModuleTypes.QUESTIONS: {
                    const questionConfig = flowModule.ModuleConfig;
                    questionConfig.QuestionBuilder.forEach((question) => {
                        const updatedAnswerObject = maybeAddUpchargesToAnswers(question, question.DefaultValue);
                        defaultCart.questionAnswers.push(updatedAnswerObject);
                    });
                    break;
                }
            }
        });
        // when loading edit flow with existing meals and a recipe type upcharge, adjust meals for recipe type upcharge.
        if (IsEditFlow && defaultCart.template && defaultCart.recipeType && defaultCart.recipeType.recipeTypePrice > 0) {
            defaultCart.template.forEach((meal) => {
                var _a, _b;
                meal.Price += (_b = (_a = defaultCart.recipeType) === null || _a === void 0 ? void 0 : _a.recipeTypePrice) !== null && _b !== void 0 ? _b : 0;
            });
        }
        // This should only be done if the meals were not already set in the MEAL_BUNDLE_CHOOSER switch case
        if (defaultCart.meals.length === 0) {
            defaultCart.meals = getMealsFromTemplate(defaultCart.template, defaultCart.daysOfWeek, defaultCart.dayQuantity, deliveryDays, defaultCart.startDate, startSunday, defaultCart.subscriptionPeriod, defaultCart.oneTimeOrderPeriod, defaultCart.isSubscription, GeneralSettings.OnlyShowDeliveryDays);
        }
        return defaultCart;
    };
    /**
     * Recursively checks all bundle combinations to find what was selected on Order Flow
     *
     * @param possibleBundles
     * @param totalSubMealQuantity
     * @param programMealsIds
     * @returns
     */
    const findBundles = (possibleBundles, requiredMeals) => {
        // 1. Pre-calculate each bundle's contribution.
        //    For every bundle, create an object mapping program meal id to the count that bundle provides.
        const bundleContributions = possibleBundles.map((bundle) => {
            const contributions = {};
            bundle.BundleMeals.forEach((bundleMeal) => {
                const programMealId = bundleMeal.ProgramMealId;
                // Only consider meals that are required.
                if (requiredMeals[programMealId] !== undefined) {
                    contributions[programMealId] = (contributions[programMealId] || 0) + bundleMeal.Quantity;
                }
            });
            return contributions;
        });
        // 2. This variable will hold the best solution found (minimal bundles used).
        let bestSolution = null;
        // 3. Helper function to check if the current provided meals exactly match the required meals.
        const isExactSolution = (providedMeals) => {
            for (const programMealId in requiredMeals) {
                const requiredQuantity = requiredMeals[+programMealId];
                if ((providedMeals[+programMealId] || 0) !== requiredQuantity) {
                    return false;
                }
            }
            return true;
        };
        /**
         * Backtracking function that explores every possible combination of bundle selections.
         *
         * @param index - Current index in possibleBundles.
         * @param providedMeals - Record of meals provided so far.
         * @param bundleQuantities - Array tracking how many times each bundle is used.
         */
        function backtrack(index, providedMeals, bundleQuantities) {
            // Base case: All bundles have been processed.
            if (index === possibleBundles.length) {
                if (isExactSolution(providedMeals)) {
                    const totalBundlesUsed = bundleQuantities.reduce((sum, qty) => sum + qty, 0);
                    if (!bestSolution || totalBundlesUsed < bestSolution.totalBundlesUsed) {
                        bestSolution = {
                            bundleQuantities: bundleQuantities.slice(),
                            totalBundlesUsed,
                        };
                    }
                }
                return;
            }
            // Prune the search if any provided quantity exceeds what is required.
            for (const programMealId in requiredMeals) {
                if ((providedMeals[+programMealId] || 0) > requiredMeals[+programMealId]) {
                    return;
                }
            }
            // Determine the maximum number of times we can use the current bundle without overshooting.
            const contributions = bundleContributions[index];
            const contributedProgramMealIds = Object.keys(contributions);
            let maxUsable = Infinity;
            if (contributedProgramMealIds.length > 0) {
                contributedProgramMealIds.forEach((id) => {
                    const programMealId = +id;
                    const contribution = contributions[programMealId];
                    const remaining = requiredMeals[programMealId] - (providedMeals[programMealId] || 0);
                    maxUsable = Math.min(maxUsable, Math.floor(remaining / contribution));
                });
            }
            else {
                // If the bundle doesn't contribute to any required meal, it isn't useful.
                maxUsable = 0;
            }
            // Try every quantity from 0 to maxUsable for this bundle.
            for (let quantity = 0; quantity <= maxUsable; quantity++) {
                bundleQuantities[index] = quantity;
                // Create a new record of provided meals by adding the current bundle's contribution.
                const newProvidedMeals = Object.assign({}, providedMeals);
                contributedProgramMealIds.forEach((id) => {
                    const programMealId = +id;
                    newProvidedMeals[programMealId] = (newProvidedMeals[programMealId] || 0) + quantity * contributions[programMealId];
                });
                // Recurse to the next bundle.
                backtrack(index + 1, newProvidedMeals, bundleQuantities);
            }
            // Optional: reset the current bundle's quantity.
            bundleQuantities[index] = 0;
        }
        // 4. Initialize the provided meals record with zeros.
        const initialProvidedMeals = {};
        Object.keys(requiredMeals).forEach((id) => {
            initialProvidedMeals[+id] = 0;
        });
        // 5. Begin backtracking with the initial state.
        backtrack(0, initialProvidedMeals, new Array(possibleBundles.length).fill(0));
        // 6. Construct the final result using the best solution.
        if (bestSolution) {
            return bestSolution.bundleQuantities
                .map((quantity, index) => {
                if (quantity > 0) {
                    return Object.assign(Object.assign({}, possibleBundles[index]), { Quantity: quantity, BundleTemplate: createTemplateFromBundle([possibleBundles[index]]) });
                }
                return null;
            })
                .filter((bundle) => bundle !== null);
        }
        return [];
    };
    /**
     * Filters and retrieves a list of valid dates based on module configurations
     *
     * @param {Cart["daysOfWeek"]} daysOfWeek - Array of days of the week (0-6) to filter by.
     * @param {Recipe[] | undefined} recipes - Array of recipes with assignments, or undefined.
     * @param {DeliveryDay[]} deliveryDays - Array of delivery days containing meal dates and ship dates.
     * @param {Cart["startDate"]} startDate - The start date of the order.
     * @param {boolean} isSubscription - Indicates if the order is a subscription.
     * @param {Cart["subscriptionPeriod"]} subscriptionPeriod - Number of weeks in the subscription period.
     * @param {Cart["oneTimeOrderPeriod"]} oneTimeOrderPeriod - Number of weeks for one-time orders.
     * @param {boolean} startSunday - Indicates whether the week starts on Sunday.
     * @param {boolean} [onlySelectedDaysOfWeek] - If true, filters to only include selected days of the week.
     * @param {boolean} [onlyDaysWithDefaultMeals] - If true, filters to only include days with recipe assignments.
     * @param {boolean} [onlyDeliveryDays] - If true, filters to only include delivery days.
     * @param {number} [dayCount] - Maximum number of days to return; defaults to `MAX_DAY_COUNT`.
     * @returns {Moment[]} A sorted array of dates filtered according to the specified criteria, capped at `dayCount`.
     */
    const getDayFilterDays = (daysOfWeek, recipes, deliveryDays, startDate, isSubscription, subscriptionPeriod, oneTimeOrderPeriod, onlySelectedDaysOfWeek, onlyDaysWithDefaultMeals, onlyDeliveryDays, dayCount, meals, showSkippedDays) => {
        const mealDates = (deliveryDays && deliveryDays.map((d) => moment(d.MealDateString))) || [];
        let newDayList = [];
        mealDates.forEach((date) => {
            // Skip if the date is before the start date.
            if (startDate && date.isBefore(startDate, "date")) {
                return;
            }
            // Check if the current date matches any selected day of the week.
            const isMealDaySelected = daysOfWeek && daysOfWeek.includes(moment(date).day());
            // Check if the current date has any recipes assigned to it.
            const dayHasRecipes = recipes &&
                recipes.some((recipe) => recipe.Assignments.find((assignment) => moment(assignment.Date).isSame(date, "date")));
            // Check if the current date is a delivery day by comparing it to deliveryDays' ShipDateString.
            const isDeliveryDay = deliveryDays.some((dd) => moment(dd.ShipDateString).isSame(date, "date"));
            if ((!onlySelectedDaysOfWeek || isMealDaySelected) && // Include if meal day is selected or not restricted.
                (!onlyDaysWithDefaultMeals || dayHasRecipes) && // Include if there are recipes for the day or not restricted.
                (!onlyDeliveryDays || isDeliveryDay) // Include if it’s a delivery day or not restricted.
            ) {
                newDayList.push(date);
            }
        });
        // Determine the end date of the order based on whether it's a subscription or one-time order.
        // If oneTimeOrderPeriod is undefined we want to grab all available days so we skip the end date filtering
        const orderPeriod = isSubscription ? subscriptionPeriod : oneTimeOrderPeriod;
        if (orderPeriod) {
            const orderPeriodInDays = orderPeriod * daysOfWeek.length;
            newDayList = newDayList.slice(0, orderPeriodInDays);
        }
        // Use configured dayCount if its set or use default value
        const finalDayCount = dayCount || MAX_DAY_COUNT;
        let result = newDayList.sort((a, b) => a - b).slice(0, finalDayCount);
        if (showSkippedDays === false && meals) {
            result = result.filter((day) => !meals.some((meal) => moment(meal.MealDate).isSame(day, "day") && meal.IsSkipped));
        }
        return result;
    };
    const getOrderEndDate = (startDate, weekStartsSunday, orderPeriod) => {
        let endDayOfWeek = 7;
        if (weekStartsSunday) {
            endDayOfWeek = 6;
        }
        else if (moment(startDate).day() === 0) {
            // If startDate is Sunday and the week doesn't start on Sundays, the StartDate is the last day of the week.
            endDayOfWeek = 0;
        }
        const endDate = moment(startDate)
            .day(endDayOfWeek)
            .add(orderPeriod ? orderPeriod - 1 : 1, "week");
        return endDate;
    };
    /**
     * This will return the cutoff date for the supplied meal date if it exists within
     * delivery days. It returns a moment in utc, so the cutoff date will still need to be converted
     * to the right timezone where necessary
     * @param date the meal date as a moment to calculate the cutoff date for
     * @param deliveryDays array of DeliverDays
     * @returns the cutoff date as a moment in utc
     */
    const getCutoffDateForMealDate = (date, deliveryDays) => {
        var _a;
        let cutoffDateMoment = moment.utc((_a = deliveryDays.find((d) => moment(d.MealDateString).isSame(date))) === null || _a === void 0 ? void 0 : _a.CutoffDate);
        return cutoffDateMoment;
    };
    /**
     * Generates an array of meal dates for a given order period, starting from a specified start date.
     * Filters and/or extends delivery days based on subscription or one-time order details.
     *
     * @param {number[]} daysOfWeek - Array representing the days of the week (0-6) to be used for the template.
     * @param {number} dayQuantity - The number of days needed in the template.
     * @param {DeliveryDay[]} deliveryDays - Array of delivery day objects containing meal and ship date strings.
     * @param {Moment|undefined} startDate - The start date for the order period as a string or Moment object.
     * @param {boolean} weekStartsSunday - Flag indicating if the week should start on Sunday.
     * @param {number} subscriptionPeriod - The period (in weeks) for a subscription-based order.
     * @param {number} oneTimeOrderPeriod - The period (in days) for a one-time order.
     * @param {boolean} isSubscription - Indicates whether the order is a subscription.
     * @returns {Moment[]} - Array of Moment objects representing the filtered and/or extended meal dates.
     */
    const getMealDatesForOrderPeriod = (daysOfWeek, dayQuantity, deliveryDays, startDate, weekStartsSunday, subscriptionPeriod, oneTimeOrderPeriod, isSubscription) => {
        var _a;
        if (!deliveryDays || !startDate || !daysOfWeek) {
            return [];
        }
        // Find the delivery date that matches the start date, and convert its ship date to a moment object.
        const deliveryDateForStartDate = moment((_a = deliveryDays.find((dd) => moment(dd.MealDateString).isSame(startDate, "date"))) === null || _a === void 0 ? void 0 : _a.ShipDateString);
        // Filter deliveryDays to only include those that are on or after the start date.
        let mealDatesForOrderPeriod = deliveryDays &&
            deliveryDays.filter((d) => moment(d.ShipDateString).isSameOrAfter(deliveryDateForStartDate, "date"));
        // If no valid meal dates remain, return an empty array.
        if (!mealDatesForOrderPeriod || !mealDatesForOrderPeriod.length || !startDate) {
            return [];
        }
        const templateDays = getTemplateDays(daysOfWeek, startDate, dayQuantity);
        const orderPeriod = isSubscription ? subscriptionPeriod : (oneTimeOrderPeriod !== null && oneTimeOrderPeriod !== void 0 ? oneTimeOrderPeriod : 1);
        // Calculate the total number of days needed by multiplying the number of days in the template by the order period.
        // Take the maximum between this value and dayQuantity to determine the final oneTimeDayQuantity.
        const oneTimeDayQuantity = Math.max(templateDays.length * orderPeriod, dayQuantity !== null && dayQuantity !== void 0 ? dayQuantity : 0);
        const extendedTemplate = [...templateDays];
        // Extend the templateDays array to match the oneTimeDayQuantity, ensuring it contains
        // the required number of days for the order by repeating the template pattern as needed.
        for (let i = templateDays.length; i < oneTimeDayQuantity; i++) {
            extendedTemplate.push(templateDays[i % templateDays.length]);
        }
        // Only calculate the end date for subscriptions, as only subscription orders should be cropped
        if (isSubscription) {
            const endDate = getOrderEndDate(startDate, weekStartsSunday, subscriptionPeriod);
            mealDatesForOrderPeriod = mealDatesForOrderPeriod.filter((date) => moment(date.ShipDateString).isSameOrBefore(endDate));
        }
        // Filter mealDatesForOrderPeriod to include only the dates that match the days specified in templateDays.
        // Then, limit the result to the number of days specified by oneTimeDayQuantity.
        mealDatesForOrderPeriod = mealDatesForOrderPeriod
            .filter((mealDate) => templateDays.some((day) => day === moment(mealDate.MealDateString).day()))
            .slice(0, oneTimeDayQuantity);
        return mealDatesForOrderPeriod.map((m) => moment(m.MealDateString));
    };
    /**
     * This function calls getMealDatesForOrderPeriod but curries the dayQuantity so each bundle can be calculated separately
     */
    const getMealDatesForBundle = (daysOfWeek, deliveryDays, startDate, weekStartsSunday, subscriptionPeriod, oneTimeOrderPeriod, isSubscription) => (dayQuantity) => getMealDatesForOrderPeriod(daysOfWeek, dayQuantity, deliveryDays, startDate, weekStartsSunday, subscriptionPeriod, oneTimeOrderPeriod, isSubscription);
    const getMealsFromTemplate = (template, daysOfWeek, dayQuantity, deliveryDays, startDate, weekStartsSunday, subscriptionPeriod, oneTimeOrderPeriod, isSubscription, onlyShowDeliveryDays, bundleMeals, existingMeals) => {
        const mealDatesForOrderPeriod = getMealDatesForOrderPeriod(daysOfWeek, dayQuantity, deliveryDays, startDate, weekStartsSunday, subscriptionPeriod, oneTimeOrderPeriod, isSubscription);
        //If we have no dates we have no meals so return empty array
        if (!mealDatesForOrderPeriod.length) {
            return [];
        }
        // If we should only show delivery days, coalesce meals into delivery days and return the result.
        if (onlyShowDeliveryDays) {
            return coalesceMealDays(mealDatesForOrderPeriod, deliveryDays, template, existingMeals, bundleMeals);
        }
        let newMeals = [];
        mealDatesForOrderPeriod.forEach((mealDate) => {
            var _a;
            const deliveryDateForMealDate = moment((_a = deliveryDays.find((dd) => moment(dd.MealDateString).isSame(mealDate, "date"))) === null || _a === void 0 ? void 0 : _a.ShipDateString);
            // Get all meals from the template that match the current day.
            const mealDayQuantities = template.filter((mdq) => mdq.DayOfWeek === moment(mealDate).day());
            // Create a new meal for each matching template meal.
            mealDayQuantities === null || mealDayQuantities === void 0 ? void 0 : mealDayQuantities.forEach((mdq) => {
                newMeals.push({
                    DeliveryDate: deliveryDateForMealDate,
                    MealDate: mealDate,
                    OriginalMealDate: mealDate,
                    Price: mdq.Price,
                    ProgramMealId: mdq.ProgramMealId,
                    ProgramMealName: mdq.ProgramMealName,
                    Quantity: mdq.Quantity,
                    NumRemovedAssignments: 0,
                    MealTypeId: mdq.MealTypeId,
                    IsSkipped: false
                });
            });
        });
        // If there are existing meals merge them with the new meals
        if (existingMeals === null || existingMeals === void 0 ? void 0 : existingMeals.length) {
            newMeals = mergeNewAndExistingMeals(newMeals, existingMeals, mealDatesForOrderPeriod);
        }
        if (bundleMeals && existingMeals) {
            return filterBundleMeals(newMeals, existingMeals, bundleMeals);
        }
        return newMeals;
    };
    /**
     * When the bundle module is used alongside a recipe module that includes meals not part of a bundle,
     * all non-bundle meals will be retained. Any meals included in the bundles will be removed.
     *
     * @param {Meal[]} newMeals - The array of new meals to which non-bundle meals will be added.
     * @param {Meal[]} existingMeals - The array of existing meals to filter against the bundle meals.
     * @param {BundleMeal[]} bundleMeals - The array of meals that are part of the bundles.
     * @returns {Meal[]} - Returns the new array of meals that includes non-bundle meals.
     */
    const filterBundleMeals = (newMeals, existingMeals, bundleMeals) => {
        const nonBundleMeals = existingMeals.filter((cartMeal) => !bundleMeals.find((bm) => bm.ProgramMealId === cartMeal.ProgramMealId));
        return [...newMeals, ...nonBundleMeals];
    };
    const getDefaultModel = (config) => {
        const { Flow: flow } = config;
        const modelProducts = clone(config.Products);
        let defaultModel = Object.assign(Object.assign({}, config), { Products: modelProducts, subscriptionPeriod: DEFAULT_SUBSCRIPTION_PERIOD, subscriptionDiscount: null, subscriptionDiscountLabelText: OrderFlowText.SubscriptionDiscountLabelText, subscriptionPeriodLabelText: OrderFlowText.SubscriptionPeriodLabelText, requirements: [] });
        flow.FlowModules.forEach((flowModule) => {
            let moduleType = flowModule.Module.ModuleType;
            switch (moduleType) {
                case ModuleTypes.REQUIREMENTS: {
                    defaultModel.requirements.push({ page: flowModule.Page, moduleConfig: flowModule.ModuleConfig });
                    break;
                }
                case ModuleTypes.RECIPE: {
                    const recipeSelectionType = flowModule.ModuleConfig.RecipeSelectionType;
                    defaultModel.recipeModuleAddMeals =
                        recipeSelectionType == RecipeSelectionType.AddMeals || recipeSelectionType == RecipeSelectionType.Both;
                    defaultModel.recipeModuleAssignToExisting =
                        recipeSelectionType == RecipeSelectionType.AssignRecipes || recipeSelectionType == RecipeSelectionType.Both;
                    break;
                }
                case ModuleTypes.RECIPE_SELECTION_WARNING: {
                    defaultModel.recipeSelectionWarningTitle = flowModule.ModuleConfig.WarningTitle;
                    defaultModel.recipeSelectionWarningMessage = flowModule.ModuleConfig.WarningMessage;
                    defaultModel.recipeSelectionMissingMealsMessage = flowModule.ModuleConfig.MissingMealsMessage;
                    break;
                }
                case ModuleTypes.RECIPE_TYPE: {
                    defaultModel.recipeTypeSelectionMode = flowModule.ModuleConfig.RecipeTypeSelectionMode;
                    break;
                }
                case ModuleTypes.MEAL_SIZE: {
                    // Set the default model's allMealSizeOption to the mod configs AllMealSizeOption, if defined.
                    // Otherwise do nothing.
                    const { AllMealSizeOption } = flowModule.ModuleConfig;
                    if (AllMealSizeOption) {
                        defaultModel.allMealSizeOption = AllMealSizeOption.TextValue;
                    }
                    break;
                }
                case ModuleTypes.HTML: {
                    break;
                }
                case ModuleTypes.SUBSCRIPTION: {
                    defaultModel.subscriptionDiscount = flowModule.ModuleConfig.SubscriptionDiscount;
                    defaultModel.subscriptionDiscountLabelText = flowModule.ModuleConfig.SubscriptionDiscountLabelText;
                    defaultModel.subscriptionPeriodLabelText = flowModule.ModuleConfig.SubscriptionPeriodLabelText;
                    break;
                }
            }
        });
        return defaultModel;
    };
    /**
     * Coalesce meal days into delivery days.
     * If the meal day is not the same as the deliveryDate, change it's deliveryDate to the delivery day for that meal day.
     *
     * @param mealDatesForOrderPeriod
     * @param deliveryDays
     * @param template
     * @returns
     */
    const coalesceMealDays = (mealDatesForOrderPeriod, deliveryDays, template, existingMeals, bundleMeals) => {
        let newMeals = [];
        //We need to update the mealDate for any meal whose mealDate != deliveryDate
        mealDatesForOrderPeriod.forEach((mealDate) => {
            var _a;
            // Find the corresponding delivery date for the current meal date.
            const deliveryDateForMealDate = moment((_a = deliveryDays.find((dd) => moment(dd.MealDateString).isSame(mealDate, "date"))) === null || _a === void 0 ? void 0 : _a.ShipDateString);
            // Get all template meals for the current day of the week.
            let mealDayQuantities = template.filter((mdq) => mdq.DayOfWeek === moment(mealDate).day());
            // For each meal quantity, create a new meal.
            mealDayQuantities === null || mealDayQuantities === void 0 ? void 0 : mealDayQuantities.forEach((mdq) => {
                var _a, _b;
                let mealDateForNewMeal = mealDate;
                // If the deliveryDateForMealDate and mealDate do not match
                // Use the deliveryDateForMealDate
                // e.g. Dont use meal days, move all meals to the delivery days
                if (moment(deliveryDateForMealDate).day() !== moment(mealDate).day()) {
                    mealDateForNewMeal = deliveryDateForMealDate;
                }
                const existingSelections = (_b = (_a = existingMeals === null || existingMeals === void 0 ? void 0 : existingMeals.find((existingMeal) => existingMeal.ProgramMealId === mdq.ProgramMealId && mdq.DayOfWeek === moment(existingMeal.MealDate).day())) === null || _a === void 0 ? void 0 : _a.RecipeAssignments) === null || _b === void 0 ? void 0 : _b.filter((assignment) => !assignment.IsAutomaticSelection);
                const adjustedAssignments = adjustAssignmentsToQuantity(existingSelections, mdq.Quantity);
                newMeals.push({
                    DeliveryDate: deliveryDateForMealDate,
                    OriginalMealDate: mealDate,
                    MealDate: mealDateForNewMeal,
                    Price: mdq.Price,
                    ProgramMealId: mdq.ProgramMealId,
                    ProgramMealName: mdq.ProgramMealName,
                    Quantity: mdq.Quantity,
                    RecipeAssignments: adjustedAssignments,
                    NumRemovedAssignments: 0,
                    MealTypeId: mdq.MealTypeId,
                    IsSkipped: false
                });
            });
        });
        // Extract the meal dates and program meal IDs from the new meals.
        const mealDates = newMeals.map((meal) => meal.MealDate);
        const programMealIds = newMeals.map((meal) => meal.ProgramMealId);
        // Iterate over the meal dates and program meal IDs to find and coalesce duplicate meals.
        mealDates.forEach((mealDate) => {
            programMealIds.forEach((programMealId) => {
                // Find all meals that share the same programMealId and mealDate.
                const duplicateProgramMeals = newMeals.filter((meal) => meal.ProgramMealId === programMealId && moment(meal.MealDate).isSame(mealDate));
                const coalescedMealDates = duplicateProgramMeals.map((m) => m.OriginalMealDate);
                // If there are multiple duplicate meals, merge them into a single meal.
                if (duplicateProgramMeals.length > 1) {
                    const coalescedMealQuantity = duplicateProgramMeals.reduce((acc, meal) => acc + meal.Quantity, 0);
                    //Remove all duplicate programMeals for mealDate
                    newMeals = newMeals.filter((meal) => meal.ProgramMealId !== programMealId || !moment(meal.MealDate).isSame(mealDate));
                    const allAssignments = duplicateProgramMeals.flatMap((m) => m.RecipeAssignments || []);
                    const adjustedAssignments = adjustAssignmentsToQuantity(allAssignments, coalescedMealQuantity);
                    // Add the coalesced meal (with summed quantity) to the list.
                    newMeals.push({
                        DeliveryDate: duplicateProgramMeals[0].DeliveryDate,
                        MealDate: mealDate,
                        OriginalMealDate: mealDate,
                        CoalescedMealDates: coalescedMealDates,
                        Price: duplicateProgramMeals[0].Price,
                        ProgramMealId: programMealId,
                        ProgramMealName: duplicateProgramMeals[0].ProgramMealName,
                        Quantity: coalescedMealQuantity,
                        RecipeAssignments: adjustedAssignments,
                        NumRemovedAssignments: 0,
                        MealTypeId: duplicateProgramMeals[0].MealTypeId,
                        IsSkipped: false
                    });
                }
            });
        });
        const newMealsWithQuantity = newMeals.filter(meal => meal.Quantity > 0);
        if (existingMeals && bundleMeals) {
            return filterBundleMeals(newMealsWithQuantity, existingMeals, bundleMeals);
        }
        return newMealsWithQuantity;
    };
    /**
     * Adjusts the total quantity of the given recipe assignments so they do not exceed the meal quantity.
     *
     * - If the total of all assignments is less than or equal to meal quantity,
     *   the original array is returned unchanged.
     * - Otherwise, a new array is returned where some assignments may be capped so that the total
     *   assignment quantity does not exceed the meal quantity.
     *
     * @param {Meal["RecipeAssignments"]} assignments - The array of recipe assignments to adjust.
     * @param {number} mealQuantity - Quantity of the meal.
     * @returns {Meal["RecipeAssignments"]} A new array of adjusted recipe assignments, or the original array if no adjustment was needed.
     */
    const adjustAssignmentsToQuantity = (assignments, mealQuantity) => {
        if (!assignments)
            return [];
        let updatedAssignments = [];
        let currentSum = 0;
        const originalTotal = assignments.reduce((sum, a) => sum + a.Quantity, 0);
        // If we're already at or below the meal quantity, no changes needed
        if (originalTotal <= mealQuantity) {
            return assignments;
        }
        // Otherwise, iterate over each assignment in order,
        // and add them until we reach the meal quantity
        for (const assignment of assignments) {
            if (currentSum >= mealQuantity) {
                // We’ve already reached our limit
                break;
            }
            const allowed = mealQuantity - currentSum; // how many more we can keep?
            if (assignment.Quantity <= allowed) {
                // Keep the entire assignment
                updatedAssignments.push(assignment);
                currentSum += assignment.Quantity;
            }
            else {
                // Keep only part of this assignment
                updatedAssignments.push(Object.assign(Object.assign({}, assignment), { Quantity: allowed }));
                currentSum += allowed;
            }
        }
        return updatedAssignments;
    };
    const getFlowModuleConfig = (flowModules, moduleName, page, sequence) => {
        var _a;
        return (_a = flowModules.find((module) => module.ModuleName === moduleName && module.Page === page && (!sequence || module.Sequence == sequence))) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    };
    return {
        synchronizeActiveCartProducts,
        synchronizeCartQuestionAnswers,
        getDefaultCart,
        getDayFilterDays,
        getCutoffDateForMealDate,
        getDefaultModel,
        getMealsFromTemplate,
        getMealDatesForOrderPeriod,
        getMealDatesForBundle,
        getFlowModuleConfig,
    };
};
